import { METRIC_OPTIONS } from './components/MetricsChart'
import { pageableCount } from './components/drilldownColumns'

const PERIODS = ['today', 'last7Days', 'last30Days', 'custom']
const VIEWS = ['chart', 'list']
const SORTS = ['asc', 'desc']
const LIMITS = ['10', '25', '50', '100']

const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/

export const EMPTY_RANGE = Object.freeze({ startDate: null, endDate: null })

export const DASHBOARD_URL_CONFIG = {
  period: { type: 'string', default: 'last30Days' },
  from: { type: 'string', default: '' },
  to: { type: 'string', default: '' },
  group: { type: 'string', default: '' },
  affiliate: { type: 'string', default: '' },
  metric: { type: 'string', default: 'clearedPayments' },
  view: { type: 'string', default: 'chart' },
  day: { type: 'string', default: '' },
  page: { type: 'number', default: 1 },
  limit: { type: 'string', default: '25' },
  sort: { type: 'string', default: 'desc' },
}

export const DASHBOARD_VIEW_KEYS = Object.keys(DASHBOARD_URL_CONFIG)

const defaultOf = (key) => DASHBOARD_URL_CONFIG[key].default

const oneOf = (allowed, value, key) => (allowed.includes(value) ? value : defaultOf(key))

const idOr = (value, fallback) => (typeof value === 'string' && ID_PATTERN.test(value) ? value : fallback)

const isMetric = (value, options = METRIC_OPTIONS) => options.some((option) => option.value === value)

const isInstant = (value) => {
  if (typeof value !== 'string' || !INSTANT_PATTERN.test(value)) return false
  const time = Date.parse(value)
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 19) === value.slice(0, 19)
}

const isCalendarDay = (value) => {
  if (typeof value !== 'string' || !DAY_PATTERN.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

const toIso = (date) => (date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : '')

export const sanitizeDashboardView = (raw, { metricOptions } = {}) => {
  const period = oneOf(PERIODS, raw.period, 'period')
  const limit = LIMITS.includes(String(raw.limit)) ? String(raw.limit) : defaultOf('limit')
  const lastPage = pageableCount(Number.MAX_SAFE_INTEGER, limit)
  const keepsRange = period === 'custom'

  return {
    period,
    from: keepsRange && isInstant(raw.from) ? raw.from : '',
    to: keepsRange && isInstant(raw.to) ? raw.to : '',
    group: idOr(raw.group, defaultOf('group')),
    affiliate: idOr(raw.affiliate, defaultOf('affiliate')),
    metric: isMetric(raw.metric, metricOptions) ? raw.metric : defaultOf('metric'),
    view: oneOf(VIEWS, raw.view, 'view'),
    day: isCalendarDay(raw.day) ? raw.day : defaultOf('day'),
    page: Number.isInteger(raw.page) && raw.page >= 1 && raw.page <= lastPage ? raw.page : defaultOf('page'),
    limit,
    sort: oneOf(SORTS, raw.sort, 'sort'),
  }
}

export const rangeOf = (view) => (
  !view.from && !view.to
    ? EMPTY_RANGE
    : { startDate: view.from ? new Date(view.from) : null, endDate: view.to ? new Date(view.to) : null }
)

export const toUrlValues = (view) => Object.fromEntries(DASHBOARD_VIEW_KEYS.map((key) => [key, view[key]]))

export const viewOf = (
  { period, range = EMPTY_RANGE, group, affiliate, metric, view, day, page, limit, sort },
  options,
) => (
  sanitizeDashboardView({
    period,
    from: toIso(range.startDate),
    to: toIso(range.endDate),
    group,
    affiliate,
    metric,
    view,
    day: day || '',
    page,
    limit,
    sort,
  }, options)
)

export const applyDashboardChange = (view, change, { effectiveMetricOf = (metric) => metric } = {}) => {
  switch (change.type) {
    case 'group':
      if (change.value === view.group) return view
      return { ...view, group: change.value, affiliate: '', page: 1 }

    case 'affiliate':
      if (change.value === view.affiliate) return view
      return { ...view, affiliate: change.value, page: 1 }

    case 'period': {
      if (change.value === view.period) return view
      const next = { ...view, period: change.value, day: '', page: 1 }
      return change.value === 'custom' ? next : { ...next, from: '', to: '' }
    }

    case 'range':
      return {
        ...view,
        from: toIso(change.value.startDate),
        to: toIso(change.value.endDate),
        day: '',
        page: 1,
      }

    case 'metric': {
      const next = { ...view, metric: change.value }
      const effectiveChanged = effectiveMetricOf(change.value) !== effectiveMetricOf(view.metric)
      return effectiveChanged ? { ...next, page: 1 } : next
    }

    case 'openDrilldown': {
      const next = { ...view, metric: change.metric, day: '', view: 'list' }
      const effectiveChanged = effectiveMetricOf(change.metric) !== effectiveMetricOf(view.metric)
      return effectiveChanged || view.day !== '' ? { ...next, page: 1 } : next
    }

    case 'openDay': {
      const next = { ...view, day: change.day, view: 'list' }
      return change.day !== view.day ? { ...next, page: 1 } : next
    }

    case 'clearDay':
      if (view.day === '') return view
      return { ...view, day: '', page: 1 }

    case 'sort':
      if (change.value === view.sort) return view
      return { ...view, sort: change.value, page: 1 }

    case 'limit': {
      const limit = String(change.value)
      if (limit === view.limit) return view
      return { ...view, limit, page: 1 }
    }

    case 'page':
      return { ...view, page: Number(change.value) }

    case 'view':
      return { ...view, view: change.value }

    case 'dropGroup':
      return { ...view, group: '', affiliate: '', page: 1 }

    case 'dropHiddenGroup':
      return { ...view, group: '' }

    case 'dropAffiliate':
      return { ...view, affiliate: '', page: 1 }

    case 'dropHiddenAffiliate':
      return { ...view, affiliate: '' }

    default:
      return view
  }
}
